<template>
  <div class="container">
    <h4 class="title is-4">Evaluations Overview</h4>
    <div class="field">
      <label class="label"
        >Search by {{ organizeByEvaluator ? 'Evaluator' : 'Submission' }} Name</label
      >
      <div class="control" v-if="organizeByEvaluator">
        <input class="input" v-model="evaluatorSearchQuery" />
      </div>
      <div class="control" v-if="organizeByApplication">
        <input class="input" v-model="applicationSearchQuery" />
      </div>
    </div>
    <div class="level">
      <div class="level-left">
        <button class="button" @click="refreshData" @keyup.enter="refreshData">Search</button>
      </div>
      <div class="level-left" v-if="organizeByEvaluator">
        <label class="label" style="marginRight: 0.5rem;">Active Only</label>
        <input
          type="checkbox"
          class="checkbox"
          style="marginRight: 1rem;"
          v-model="activeOnly"
          @change="toggleActiveOnly"
        />
      </div>
      <div class="level-left" v-if="organizeByEvaluator">
        <label class="label" style="marginRight: 0.5rem;">Confirmed Only</label>
        <input
          type="checkbox"
          class="checkbox"
          style="marginRight: 1rem;"
          v-model="confirmOnly"
          @change="toggleConfirmedOnly"
        />
      </div>
    </div>
    <hr />
    <div class="field">
      <div class="control">
        <button class="button" @click="swap" v-if="organizeByEvaluator">View by Submission</button>
        <button class="button" @click="swap" v-if="organizeByApplication">View by Evaluator</button>
      </div>
    </div>
    <div class="content">
      <span v-if="evaluatorFilters.name"
        ><em>Searching for "{{ evaluatorFilters.name }}"</em></span
      >
      <span v-if="applicationFilters.search"
        ><em>Searching for "{{ applicationFilters.search }}"</em></span
      >
    </div>
    <div v-if="dataLoaded && organizeByApplication">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div class="select">
              <select v-model="applicationFilters.pageSize" @change="refreshData()">
                <option :value="20">20 per page</option>
                <option :value="50">50 per page</option>
                <option :value="100">100 per page</option>
                <option :value="250">250 per page</option>
                <option :value="999999">All results</option>
              </select>
            </div>
          </div>
        </div>
        <div class="level-right">
          <button class="button">
            <router-link :to="{ name: 'StaffAddEvaluator' }">Add Evaluator</router-link>
          </button>
        </div>
      </div>
      <Pagination
        :pagination-details="applicationPaginationInformation"
        @update="updateApplicationPaginationDetails($event)"
      ></Pagination>
    </div>
    <div v-if="dataLoaded && organizeByEvaluator">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div class="select">
              <select v-model="evaluatorFilters.pageSize" @change="refreshData()">
                <option :value="20">20 per page</option>
                <option :value="50">50 per page</option>
                <option :value="100">100 per page</option>
                <option :value="250">250 per page</option>
                <option :value="999999">All results</option>
              </select>
            </div>
          </div>
        </div>
        <div class="level-right">
          <button class="button">
            <router-link :to="{ name: 'StaffAddEvaluator' }">Add Evaluator</router-link>
          </button>
        </div>
      </div>
      <Pagination
        v-if="dataLoaded && organizeByEvaluator"
        :pagination-details="evaluatorPaginationInformation"
        @update="updateEvaluatorPaginationDetails($event)"
      ></Pagination>
    </div>
    <table class="table is-fullwidth is-striped" v-if="dataLoaded && organizeByApplication">
      <thead>
        <tr>
          <th class="fixed-width">Organization Name</th>
          <th class="fixed-width">Project Name</th>
          <th class="evals">Evaluators</th>
          <th class="fixed-width orgSub">Organization Submitted To</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="application in applications" :key="application.id">
          <th>
            <router-link
              :to="{
                name: 'StaffApplicationReviewDetail',
                params: { applicationId: application.id },
              }"
              > {{ application.organization ? application.organization.name : 'No organization' }}</router-link
            >
            <span class="icon" v-if="application.isLoading">
              <i class="fa fa-refresh fa-spin"></i>
            </span>
          </th>
          <td>
            {{ application.projectName}}
          </td>
          <td class="evals">
            <div class="field is-grouped is-grouped-multiline">
              <div
                class="control"
                v-for="evaluator in application.evaluatorsRef"
                :key="evaluator.id"
              >
                <div class="tags has-addons">
                  <router-link
                    :to="{ name: 'EvaluatorDetail', params: { evaluatorId: evaluator.id } }"
                    class="tag"
                    target="_blank"
                    >{{ evaluator.fullName }}</router-link
                  >
                  <a
                    class="tag is-delete"
                    v-if="!application.isLoading"
                    @click="removeEvaluatorFromApplication(application, evaluator.id)"
                  ></a>
                  <a class="tag is-delete disabledDelete" v-if="application.isLoading"></a>
                </div>
              </div>
              <span class="tag" @click="toggleModal(null, application)">+</span>
            </div>
          </td>
          <td class="orgSub">
            {{ application.organizationSubmittedTo}}
          </td>
        </tr>
      </tbody>
    </table>
    <table class="table is-fullwidth is-striped" v-if="dataLoaded && organizeByEvaluator">
      <thead>
        <tr>
          <th class="fixed-width">Evaluator Name</th>
          <th>Submissions</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="evaluator in evaluators" :key="evaluator.id">
          <th>
            <router-link
              :to="{ name: 'EvaluatorDetail', params: { evaluatorId: evaluator.id } }"
              target="_blank"
              >{{ evaluator.fullName }}</router-link
            >
            <span class="icon" v-if="evaluator.isLoading">
              <i class="fa fa-refresh fa-spin"></i>
            </span>
          </th>
          <td>
            <div class="field is-grouped is-grouped-multiline">
              <div
                class="control"
                v-for="assignment in evaluator.currentAssignments"
                :key="assignment.id"
              >
                <div class="tags has-addons">
                  <router-link
                    :to="{
                      name: 'StaffApplicationReviewDetail',
                      params: { applicationId: assignment.id },
                    }"
                    class="tag"
                  >
                    {{ assignment.organizationName }}
                  </router-link>
                  <a
                    class="tag is-delete"
                    @click="removeApplicationFromEvaluator(evaluator, assignment.id)"
                    v-if="!evaluator.isLoading"
                  ></a>
                  <a class="tag is-delete disabledDelete" v-if="evaluator.isLoading"></a>
                </div>
              </div>
              <span class="tag" @click="toggleModal(evaluator, null)">+</span>
            </div>
          </td>
          <td>
            {{ evaluator.status }}
          </td>
        </tr>
      </tbody>
    </table>
    <div class="content">
      <loading-box v-if="!dataLoaded"></loading-box>
      <span v-if="dataLoaded">
        <span v-if="organizeByApplication && applications.length === 0"
          ><em>No Submissions Found</em></span
        >
        <span v-if="organizeByEvaluator && evaluators.length === 0"
          ><em>No Evaluators Found</em></span
        >
      </span>
    </div>
    <div class="modal is-active" v-if="showModal">
      <div class="modal-background"></div>
      <div class="modal-card" :style="{ height: '35rem', width: '50rem' }">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Add {{ organizeByEvaluator ? 'Application' : 'Evaluator' }} for
            {{ modalEvaluator ? modalEvaluator.fullName : modalApplication.projectName }}
          </p>
          <button class="delete" aria-label="close" @click="toggleModal"></button>
        </header>
        <section class="modal-card-body">
          <div class="select" v-if="organizeByEvaluator">
            <multiselect
              v-model="modalSelectedApplications"
              deselect-label="Remove this application"
              track-by="id"
              label="organizationName"
              :multiple="true"
              :options="modalApplications"
              :searchable="true"
              :close-on-select="false"
            >
              <template slot="singleLabel" slot-scope="{ option }"
                ><strong>{{ option.name }}</strong> ></template
              >
            </multiselect>
          </div>
          <div class="select" v-if="organizeByApplication">
            <multiselect
              v-model="modalSelectedEvaluators"
              deselect-label="Remove this evaluator"
              track-by="id"
              label="fullName"
              :multiple="true"
              :options="modalEvaluators"
              :searchable="true"
              :close-on-select="false"
            >
              <template slot="singleLabel" slot-scope="{ option }"
                ><strong>{{ option.projectName }}</strong> ></template
              >
            </multiselect>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button
            class="button is-primary"
            v-if="organizeByEvaluator"
            @click="updateApplicationsForEvaluator(modalEvaluator, modalSelectedApplications)"
            :disabled="!modalSelectedApplications.length"
          >
            Save applications
          </button>
          <button
            class="button is-primary"
            v-if="organizeByApplication"
            @click="updateEvaluatorsForApplication(modalApplication, modalSelectedEvaluators)"
            :disabled="!modalSelectedEvaluators.length"
          >
            Save evaluators
          </button>
          <button class="button" @click="toggleModal">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'

import User from '@/services/users'
import Application from '@/services/applications'
import Cohort from '@/services/cohorts'
import LoadingBox from '@/components/LoadingBox'

import Pagination from '@/components/Pagination'
import { getPaginationDataFromResponse } from '@/services/pagination'

export default {
  components: { LoadingBox, Pagination, Multiselect },
  data() {
    return {
      evaluatorFilters: {
        isEvaluator: true,
        name: '',
        pageSize: 20,
        page: null,
        status: 'CONFIRMED,APPLIED,ACTIVE',
      },
      evaluatorSearchQuery: '',
      applicationFilters: {
        search: '',
        pageSize: 20,
        page: null,
      },
      allEvaluatorFilters: {
        isEvaluator: true,
        pageSize: 1000,
      },
      allEvaluators: [],
      allApplicationFilters: {
        pageSize: 1000,
      },
      allApplications: [],
      applicationSearchQuery: '',
      applications: [],
      evaluators: [],
      evaluatorDataLoaded: false,
      applicationDataLoaded: false,
      organizeByApplication: false,
      organizeByEvaluator: true,
      showModal: false,
      modalApplication: null,
      modalSelectedApplications: null,
      modalEvaluator: null,
      modalSelectedEvaluators: null,
      evaluatorPaginationInformation: {},
      applicationPaginationInformation: {},
      dataLoaded: false,
      allApplicationsPromise: null,
      allEvaluatorsPromise: null,
      confirmOnly: false,
      activeOnly: false,
    }
  },
  computed: {
    modalEvaluators() {
    //console.log("modalEvaluators");
      if (!this.modalApplication || this.allEvaluators.length === 0) {
        return null
      }
      return this.allEvaluators.filter(e => !this.modalApplication.evaluators.includes(e.id))
    },
    modalApplications() {
      if (!this.modalEvaluator || this.allApplications.length === 0) {
        return null
      }
      const unassignedApps = this.allApplications.filter(
        application => !this.modalEvaluator.assignments.includes(application.id),
      )
      const unassignedAppsInCohort = unassignedApps.filter(
        application => application.cohort === this.activeCohortID,
      )
      return unassignedAppsInCohort
    },
  },
  async created() {
    //console.log("Created");
    this.dataLoaded = false
    this.refreshDataLoading()

    this.activeCohortID = await Cohort.api.activeID()
    this.allApplicationsPromise = Application.api.getAllApplications().then(data => {
      this.allApplications = data
    })
    this.allEvaluatorsPromise = User.api.getAllEvaluators().then(data => {
      this.allEvaluators = data
    })
  },
  methods: {
    toggleActiveOnly() {
      if (this.activeOnly) {
        this.confirmOnly = false
        this.evaluatorFilters.status = 'ACTIVE'
      } else {
        if (!this.confirmOnly) {
          this.evaluatorFilters.status = 'CONFIRMED,APPLIED,ACTIVE'
        }
      }
    },
    toggleConfirmedOnly() {
      if (this.confirmOnly) {
        this.activeOnly = false
        this.evaluatorFilters.status = 'CONFIRMED'
      } else {
        if (!this.activeOnly) {
          this.evaluatorFilters.status = 'CONFIRMED,APPLIED,ACTIVE'
        }
      }
    },
    updateEvaluatorPaginationDetails(pageNumber) {
      this.evaluatorFilters.page = pageNumber
      this.refreshDataLoading()
    },
    updateApplicationPaginationDetails(pageNumber) {
      this.applicationFilters.page = pageNumber
      this.refreshDataLoading()
    },
    refreshDataLoading() {
      this.dataLoaded = false
      this.refreshData()
    },
    refreshData() {
      if (this.applicationSearchQuery) {
        this.applicationFilters.search = this.applicationSearchQuery
      }
      let applicationFiltersWithPage = {}
      if (this.applicationFilters.page) {
        applicationFiltersWithPage = {
          ...this.applicationFilters,
          page: this.applicationFilters.page,
        }
      } else {
        applicationFiltersWithPage = { ...this.applicationFilters }
      }
      let applicationPromise = Application.api
        .list({ filters: applicationFiltersWithPage })
        .then(data => {
          this.applications = data.results
          this.applicationPaginationInformation = getPaginationDataFromResponse(data)
          this.clearSearchQueries()
        })
      if (this.evaluatorSearchQuery) {
        this.evaluatorFilters.name = this.evaluatorSearchQuery
      }
      let evaluatorFiltersWithPage = {}
      if (this.evaluatorFilters.page) {
        evaluatorFiltersWithPage = {
          ...this.evaluatorFilters,
          page: this.evaluatorFilters.page,
        }
      } else {
        evaluatorFiltersWithPage = { ...this.evaluatorFilters }
      }
      let evaluatorPromise = User.api.list({ filters: evaluatorFiltersWithPage }).then(data => {
        this.evaluators = data.results
        //console.log("this.evaluators")
        //console.log(this.evaluators)
        this.evaluatorPaginationInformation = getPaginationDataFromResponse(data)
        this.clearSearchQueries()
      })
      Promise.all([
        applicationPromise,
        evaluatorPromise,
        this.allApplicationsPromise,
        this.allEvaluatorsPromise,
      ]).then(() => {
        this.dataLoaded = true
      })
    },
    swap() {
      this.applicationFilters.search = ''
      this.evaluatorFilters.name = ''
      this.clearSearchQueries()
      this.organizeByApplication = !this.organizeByApplication
      this.organizeByEvaluator = !this.organizeByEvaluator
    },
    clearSearchQueries() {
      this.applicationSearchQuery = ''
      this.evaluatorSearchQuery = ''
    },
    addApplicationToEvaluator(evaluator, applicationId) {
      evaluator['isLoading'] = true
      const evaluatorIndex = this.evaluators.findIndex(e => e.id === evaluator.id)
      var oldAssignments = [...this.evaluators[evaluatorIndex].assignments]
      var oldcurrentAssignments = [...this.evaluators[evaluatorIndex].currentAssignments]
      const assignmentObject = this.allApplications.find(a => a.id === applicationId)
      const assignmentRefObject = {
        id: assignmentObject.id,
        name: assignmentObject.name,
      }
      this.evaluators[evaluatorIndex].assignments.push(assignmentObject.id)
      this.evaluators[evaluatorIndex].currentAssignments.push(assignmentRefObject)
      this.evaluators[evaluatorIndex].currentAssignments.sort((a, b) => {
        const nameA = a.name.toLowerCase()
        const nameB = b.name.toLowerCase()
        if (nameA < nameB)
          //sort string ascending
          return -1
        if (nameA > nameB) return 1
        return 0
      })

      const newEvaluatorAssignments = evaluator.assignments
      newEvaluatorAssignments.push(applicationId)
      let newEvaluator = evaluator
      newEvaluator.assignments = newEvaluatorAssignments
      const fields = ['assignments']
      this.toggleModal()
      User.api
        .update(newEvaluator, fields)
        .then(() => {
          this.refreshData()
        })
        .catch(() => {
          this.evaluators[evaluatorIndex].assignments = oldAssignments
          this.evaluators[evaluatorIndex].currentAssignments = oldcurrentAssignments
        })
    },
    removeApplicationFromEvaluator(evaluator, applicationId) {
      evaluator['isLoading'] = true
      const evaluatorIndex = this.evaluators.findIndex(e => e.id === evaluator.id)
      var oldAssignments = [...this.evaluators[evaluatorIndex].assignments]
      var newAssignments = this.evaluators[evaluatorIndex].assignments.filter(
        e => e !== applicationId,
      )
      var oldcurrentAssignments = [...this.evaluators[evaluatorIndex].currentAssignments]
      var newcurrentAssignments = this.evaluators[evaluatorIndex].currentAssignments.filter(
        e => e.id !== applicationId,
      )
      this.evaluators[evaluatorIndex].assignments = newAssignments
      this.evaluators[evaluatorIndex].currentAssignments = newcurrentAssignments
      const newEvaluatorAssignments = evaluator.assignments.filter(
        assignment => assignment !== applicationId,
      )
      let newEvaluator = evaluator
      newEvaluator.assignments = newEvaluatorAssignments
      const fields = ['assignments']
      User.api
        .update(newEvaluator, fields)
        .then(() => {
          this.refreshData()
        })
        .catch(() => {
          this.evaluators[evaluatorIndex].assignments = oldAssignments
          this.evaluators[evaluatorIndex].assignmentRefs = oldcurrentAssignments
        })
    },
    addEvaluatorToApplication(application, evaluatorId) {
      application['isLoading'] = true
      const applicationIndex = this.applications.findIndex(e => e.id === application.id)
      var oldEvaluators = [...this.applications[applicationIndex].evaluators]
      var oldEvaluatorsRef = [...this.applications[applicationIndex].evaluatorsRef]
      const evaluatorObject = this.allEvaluators.find(e => e.id === evaluatorId)
      const evaluatorRefObject = {
        id: evaluatorObject.id,
        fullName: evaluatorObject.fullName,
        email: evaluatorObject.email,
      }
      this.applications[applicationIndex].evaluators.push(evaluatorObject.id)
      this.applications[applicationIndex].evaluatorsRef.push(evaluatorRefObject)
      this.applications[applicationIndex].evaluatorsRef.sort((a, b) => {
        const nameA = a.fullName.toLowerCase()
        const nameB = b.fullName.toLowerCase()
        if (nameA < nameB)
          //sort string ascending
          return -1
        if (nameA > nameB) return 1
        return 0
      })

      const newApplicationAssignments = application.evaluators
      newApplicationAssignments.push(evaluatorId)
      let newApplication = application
      newApplication.evaluators = newApplicationAssignments
      const fields = ['evaluators']
      this.toggleModal()
      Application.api
        .update(newApplication, fields)
        .then(() => {
          this.refreshData()
        })
        .catch(() => {
          this.applications[applicationIndex].evaluators = oldEvaluators
          this.applications[applicationIndex].evaluatorsRef = oldEvaluatorsRef
        })
    },
    removeEvaluatorFromApplication(application, evaluatorId) {
      application['isLoading'] = true
      const applicationIndex = this.applications.findIndex(e => e.id === application.id)
      var oldEvaluators = [...this.applications[applicationIndex].evaluators]
      var newEvaluators = this.applications[applicationIndex].evaluators.filter(
        e => e !== evaluatorId,
      )
      var oldEvaluatorsRef = [...this.applications[applicationIndex].evaluatorsRef]
      var newEvaluatorsRef = this.applications[applicationIndex].evaluatorsRef.filter(
        e => e.id !== evaluatorId,
      )
      this.applications[applicationIndex].evaluators = newEvaluators
      this.applications[applicationIndex].evaluatorsRef = newEvaluatorsRef
      const newApplicationAssignments = application.evaluators.filter(
        evaluator => evaluator !== evaluatorId,
      )
      let newApplication = application
      newApplication.evaluators = newApplicationAssignments
      const fields = ['evaluators']
      Application.api
        .update(newApplication, fields)
        .then(() => {
          this.refreshData()
        })
        .catch(() => {
          this.applications[applicationIndex].evaluators = oldEvaluators
          this.applications[applicationIndex].evaluatorsRef = oldEvaluatorsRef
        })
    },
    updateEvaluatorsForApplication(application, evaluators) {
      application['isLoading'] = true
      const oldEvaluators = application.evaluators
      const oldEvaluatorsRef = application.evaluatorsRef
      const newEvaluators = evaluators.map(e => e.id)
      const newApplication = application.copy()
      newApplication.evaluators = newEvaluators
      const fields = ['evaluators']
      Application.api
        .update(newApplication, fields)
        .then(() => {
          this.refreshData()
          this.toggleModal()
        })
        .catch(() => {
          const applicationIndex = this.applications.findIndex(e => e.id === application.id)
          this.applications[applicationIndex].evaluators = oldEvaluators
          this.applications[applicationIndex].evaluatorsRef = oldEvaluatorsRef
        })
    },
    updateApplicationsForEvaluator(evaluator, applications) {
      evaluator['isLoading'] = true
      const oldAssignments = evaluator.assignments
      const oldAssignmentRefs = evaluator.assignmentRefs
      const newAssignments = applications.map(a => a.id)
      const newEvaluator = evaluator.copy()
      newEvaluator.assignments = newAssignments
      const fields = ['assignments']
      User.api
        .update(newEvaluator, fields)
        .then(() => {
          this.refreshData()
          this.toggleModal()
        })
        .catch(() => {
          evaluator.assignments = oldAssignments
          evaluator.assignmentRefs = oldAssignmentRefs
        })
    },
    toggleModal(evaluator, application) {
      if (evaluator) {
        this.modalEvaluator = evaluator
        this.modalSelectedApplications = evaluator.currentAssignments
      }

      if (application) {
        this.modalApplication = application
        this.modalSelectedEvaluators = application.evaluatorsRef
      }

      if (this.showModal) {
        this.modalApplication = null
        this.modalEvaluator = null
        this.modalSelectedApplications = []
        this.modalSelectedEvaluators = []
      }
      this.showModal = !this.showModal
    },
  },
}
</script>

<style lang="scss" scoped>
.tag {
  cursor: pointer;
  margin: 0 2px;
}

.fixed-width {
  word-wrap: break-word;
  width: 200px;
}

.multiselect {
  min-width: 25rem;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.disabledDelete {
  color: rgb(177, 177, 177);
}

.orgSub {
  max-width: 130px !important;
}

.evals {
  min-width: 280px !important;
}
</style>
